import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip, useMap } from "react-leaflet";
import { AlertTriangle, CloudSun, MapPin, Wrench, Loader2, Layers, Crosshair } from "lucide-react";
import "leaflet/dist/leaflet.css";
import { api, formatApiErrorDetail } from "@/lib/api";

const SEVERITY = {
  extreme: { color: "#b91c1c", label: "Extreme" },
  severe: { color: "#ea580c", label: "Severe" },
  moderate: { color: "#d4af37", label: "Moderate" },
  minor: { color: "#2563eb", label: "Minor" },
  none: { color: "#1b2a63", label: "Clear" },
};
// The weather service reports a quiet property as "normal"; treat anything unrecognised as clear.
const sev = (key) => SEVERITY[key] || SEVERITY.none;

// Keyless tile providers only — no API keys to leak, nothing to expire.
const TILES = {
  streets: { url: "https://tile.openstreetmap.org/{z}/{x}/{y}.png", label: "Streets",
             attribution: '&copy; OpenStreetMap contributors' },
  dark: { url: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}",
          label: "Dark", attribution: "Tiles &copy; Esri" },
  satellite: { url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
               label: "Satellite", attribution: "Tiles &copy; Esri" },
};

const pinColor = (p) => {
  if (p.urgent_jobs > 0 || p.open_issues > 0) return "#ea580c";
  return sev(p.severity).color;
};
const pinRadius = (p) => 7 + Math.min(p.open_jobs, 6) * 1.6;

/** Keeps the viewport on the portfolio whenever the set of located properties changes. */
const FitBounds = ({ bounds, token }) => {
  const map = useMap();
  useEffect(() => {
    if (!bounds) return;
    map.fitBounds([[bounds.south, bounds.west], [bounds.north, bounds.east]],
                  { padding: [48, 48], maxZoom: 12 });
  }, [map, bounds, token]);
  return null;
};

const Stat = ({ label, value, testId, tone = "text-royal-deep" }) => (
  <div className="rounded-lg border border-border bg-white px-3 py-2.5" data-testid={testId}>
    <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
    <p className={`font-display text-xl font-bold mt-0.5 ${tone}`}>{value}</p>
  </div>
);

export default function OperationsMap() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [basemap, setBasemap] = useState("streets");
  const [onlyAlerts, setOnlyAlerts] = useState(false);
  const mapRef = useRef(null);

  useEffect(() => {
    // Must go through the shared client: it carries the bearer-token fallback and X-Org-Id. A bare
    // fetch() relied on the cross-site cookie alone, which iOS Safari blocks — which is why this
    // page failed on iPhone while working on desktop.
    api.get("/map")
      .then(({ data: d }) => setData(d))
      .catch((e) => setError(e.response
        ? formatApiErrorDetail(e.response.data?.detail)
        : "Could not load the operations map — check your connection and try again."));
  }, []);

  const shown = useMemo(() => {
    const rows = (data?.properties || []).filter((p) => p.located);
    return onlyAlerts ? rows.filter((p) => p.alerts?.length || p.urgent_jobs || p.open_issues) : rows;
  }, [data, onlyAlerts]);

  const center = data?.bounds
    ? [(data.bounds.south + data.bounds.north) / 2, (data.bounds.west + data.bounds.east) / 2]
    : [35.9, -75.7];

  if (error) {
    return (
      <div className="p-6" data-testid="map-page">
        <p className="text-sm text-destructive" data-testid="map-error">{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex h-64 items-center justify-center" data-testid="map-loading">
        <Loader2 className="h-5 w-5 animate-spin text-royal" />
      </div>
    );
  }

  const c = data.counts;

  return (
    <div className="space-y-4" data-testid="map-page">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
          Operations map
        </p>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-royal-deep mt-1">
          Your whole portfolio, located
        </h1>
        <p className="text-sm text-muted-foreground mt-1.5">
          Every property plotted from its own address, with live National Weather Service
          conditions and any active tropical systems on top.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4" data-testid="map-stats">
        <Stat label="Properties" value={c.total} testId="map-stat-total" />
        <Stat label="Located" value={c.located} testId="map-stat-located" />
        <Stat label="Weather alerts" value={c.with_alerts} testId="map-stat-alerts"
              tone={c.with_alerts ? "text-orange-600" : "text-royal-deep"} />
        <Stat label="Open work orders" value={c.open_jobs} testId="map-stat-jobs" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-full border border-border bg-secondary/60 p-1"
             data-testid="basemap-toggle">
          {Object.entries(TILES).map(([key, t]) => (
            <button key={key} type="button" onClick={() => setBasemap(key)}
              data-testid={`basemap-${key}`} aria-pressed={basemap === key}
              className={`rounded-full px-3 min-h-[36px] text-xs font-semibold transition-colors duration-200
                          ${basemap === key ? "bg-royal text-white" : "text-royal-deep hover:text-royal"}`}>
              <Layers className="mr-1.5 inline h-3 w-3" />{t.label}
            </button>
          ))}
        </div>
        <button type="button" onClick={() => setOnlyAlerts((v) => !v)}
          data-testid="toggle-needs-attention" aria-pressed={onlyAlerts}
          className={`rounded-full border px-3.5 min-h-[36px] text-xs font-semibold transition-colors duration-200
                      ${onlyAlerts ? "border-orange-500 bg-orange-50 text-orange-700"
                                   : "border-border bg-white text-royal-deep hover:text-royal"}`}>
          <AlertTriangle className="mr-1.5 inline h-3 w-3" />Needs attention only
        </button>
        <button type="button" data-testid="map-refit"
          onClick={() => data.bounds && mapRef.current?.fitBounds(
            [[data.bounds.south, data.bounds.west], [data.bounds.north, data.bounds.east]],
            { padding: [48, 48], maxZoom: 12 })}
          className="rounded-full border border-border bg-white px-3.5 min-h-[36px] text-xs
                     font-semibold text-royal-deep transition-colors duration-200 hover:text-royal">
          <Crosshair className="mr-1.5 inline h-3 w-3" />Fit portfolio
        </button>
        <span className="text-xs text-muted-foreground" data-testid="map-shown-count">
          Showing {shown.length} of {c.located}
        </span>
      </div>

      <div className="overflow-hidden rounded-xl border border-border" data-testid="map-canvas">
        <MapContainer center={center} zoom={9} scrollWheelZoom
                      ref={mapRef}
                      style={{ height: "clamp(320px, 58vh, 620px)", width: "100%" }}>
          <TileLayer key={basemap} url={TILES[basemap].url}
                     attribution={TILES[basemap].attribution} />
          <FitBounds bounds={data.bounds} token={basemap} />

          {/* Active tropical systems, straight from the National Hurricane Center feed. */}
          {(data.storms || []).map((s) => (
            <CircleMarker key={`${s.name}-${s.lat}-${s.lon}`} center={[s.lat, s.lon]} radius={16}
              pathOptions={{ color: "#b91c1c", fillColor: "#b91c1c", fillOpacity: 0.22, weight: 2,
                             dashArray: "4 4" }}>
              <Tooltip permanent direction="top" offset={[0, -8]}>
                <span className="text-[11px] font-bold">
                  {s.classification || "Storm"} {s.name}
                  {s.wind_mph ? ` · ${s.wind_mph} mph` : ""}
                </span>
              </Tooltip>
            </CircleMarker>
          ))}

          {shown.map((p) => (
            <CircleMarker key={p.id} center={[p.lat, p.lon]} radius={pinRadius(p)}
              pathOptions={{ color: "#ffffff", weight: 2, fillColor: pinColor(p), fillOpacity: 0.95 }}>
              <Tooltip direction="top" offset={[0, -6]}>
                <span className="text-[11px] font-semibold">{p.name}</span>
              </Tooltip>
              <Popup>
                <div className="min-w-[200px]" data-testid={`map-popup-${p.id}`}>
                  <p className="font-display text-sm font-bold text-royal-deep">{p.name}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {p.address}{p.city ? `, ${p.city}` : ""}{p.state ? `, ${p.state}` : ""}
                  </p>
                  <div className="mt-2 space-y-1 text-[11px]">
                    {p.temperature_f != null && (
                      <p className="flex items-center gap-1.5 text-royal-deep">
                        <CloudSun className="h-3 w-3 text-royal" />
                        {Math.round(p.temperature_f)}&deg;F{p.conditions ? ` · ${p.conditions}` : ""}
                      </p>
                    )}
                    <p className="flex items-center gap-1.5 text-royal-deep">
                      <Wrench className="h-3 w-3 text-royal" />
                      {p.open_jobs} open work order{p.open_jobs === 1 ? "" : "s"}
                      {p.urgent_jobs ? ` · ${p.urgent_jobs} urgent` : ""}
                    </p>
                    {p.open_issues > 0 && (
                      <p className="flex items-center gap-1.5 text-orange-700 font-semibold">
                        <AlertTriangle className="h-3 w-3" />
                        {p.open_issues} open issue{p.open_issues === 1 ? "" : "s"}
                      </p>
                    )}
                    {(p.alerts || []).map((a) => (
                      <p key={a.event} className="flex items-center gap-1.5 font-semibold"
                         style={{ color: sev(p.severity).color }}>
                        <AlertTriangle className="h-3 w-3" />{a.event}
                      </p>
                    ))}
                  </div>
                  <Link to={`/properties/${p.id}`} data-testid={`map-open-${p.id}`}
                    className="mt-2.5 inline-block text-[11px] font-bold uppercase tracking-[0.12em]
                               text-royal hover:underline">
                    Open property
                  </Link>
                </div>
              </Popup>
            </CircleMarker>
          ))}
        </MapContainer>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="map-legend">
        {Object.entries(SEVERITY).map(([key, s]) => (
          <span key={key} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: s.color }} />
            {s.label}
          </span>
        ))}
        <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <span className="h-2.5 w-2.5 rounded-full bg-orange-600" />Needs attention
        </span>
        <span className="text-[11px] text-muted-foreground">Pin size reflects open work orders.</span>
      </div>

      {c.unlocated > 0 && (
        <p className="text-[11px] text-muted-foreground" data-testid="map-unlocated-note">
          <MapPin className="mr-1 inline h-3 w-3" />
          {c.unlocated} propert{c.unlocated === 1 ? "y has" : "ies have"} no coordinates yet — they
          are located automatically on the next weather refresh.
        </p>
      )}
    </div>
  );
}
